/**
 * KbTopicsEditor — разделы базы знаний агента: создать, переименовать, править,
 * удалить.
 *
 * Вынесен из SelfAgentPage, когда у агентов появились специалисты (08.08):
 * разделы у основного агента и у специалиста устроены одинаково, и держать две
 * копии этой формы означало бы чинить каждую правку дважды.
 *
 * Компонент не знает, чьи это разделы. Он получает список и функцию сохранения —
 * а куда они уедут (реестр нашей Тони или специалиста), решает страница.
 */

import { useState } from 'react'
import { Loader2, Pencil, PencilLine, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'

export interface KbTopic {
  topic: string
  title?: string
  content_md?: string
}

/** Технический ключ из человеческого названия — латиницей, как на сервере. */
export function slugifyTopic(title: string): string {
  const map: Record<string, string> = {
    а:'a',б:'b',в:'v',г:'g',д:'d',е:'e',ё:'yo',ж:'zh',з:'z',и:'i',й:'y',к:'k',
    л:'l',м:'m',н:'n',о:'o',п:'p',р:'r',с:'s',т:'t',у:'u',ф:'f',х:'h',ц:'ts',
    ч:'ch',ш:'sh',щ:'sch',ъ:'',ы:'y',ь:'',э:'e',ю:'yu',я:'ya',
  }
  const base = title.toLowerCase().split('').map((c) => map[c] ?? c).join('')
    .replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40)
  return base && /^[a-z]/.test(base) ? base : `razdel_${Date.now().toString(36)}`
}

interface Props {
  topics: KbTopic[]
  /** Сохранить новый состав разделов целиком. */
  onPersist: (next: KbTopic[]) => Promise<void>
  saving: boolean
  /** Подсказка в форме создания — у специалиста пример другой. */
  placeholder?: string
  emptyHint?: string
}

export function KbTopicsEditor({
  topics, onPersist, saving,
  placeholder = 'Например: Возражения и ответы',
  emptyHint = 'Разделов нет.',
}: Props) {
  const [openTopic, setOpenTopic] = useState<string | null>(null)
  const [topicDraft, setTopicDraft] = useState('')
  const [renaming, setRenaming] = useState<string | null>(null)
  const [renameDraft, setRenameDraft] = useState('')
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')

  async function addTopic() {
    const title = newName.trim()
    if (!title) return
    let slug = slugifyTopic(title)
    const taken = new Set(topics.map((t) => t.topic))
    let n = 2
    while (taken.has(slug)) slug = `${slugifyTopic(title).slice(0, 36)}_${n++}`
    await onPersist([...topics, { topic: slug, title, content_md: `# ${title}\n\n` }])
    setCreating(false); setNewName('')
    setOpenTopic(slug); setTopicDraft(`# ${title}\n\n`)
  }

  async function renameTopic(name: string) {
    const title = renameDraft.trim()
    if (!title) return
    await onPersist(topics.map((t) => (t.topic === name ? { ...t, title } : t)))
    setRenaming(null)
  }

  async function removeTopic(name: string) {
    await onPersist(topics.filter((t) => t.topic !== name))
    if (openTopic === name) setOpenTopic(null)
  }

  async function saveTopic(name: string) {
    await onPersist(topics.map((t) => (t.topic === name ? { ...t, content_md: topicDraft } : t)))
    setOpenTopic(null)
  }

  return (
    <div className="divide-y rounded-md border">
      {topics.length === 0 ? (
        <p className="px-3 py-6 text-center text-xs text-muted-foreground">{emptyHint}</p>
      ) : topics.map((t) => (
        <div key={t.topic}>
          <div className="flex items-center gap-1 pr-2">
            <button
              type="button"
              className="flex flex-1 items-center justify-between px-3 py-2 text-left hover:bg-slate-50"
              onClick={() => {
                if (openTopic === t.topic) { setOpenTopic(null); return }
                setOpenTopic(t.topic)
                setTopicDraft(t.content_md || '')
              }}
            >
              <span className="text-sm">{t.title || t.topic}</span>
              <span className="text-[11px] text-muted-foreground">
                {(t.content_md || '').length.toLocaleString('ru-RU')} знаков
              </span>
            </button>
            <button
              type="button"
              title="Переименовать раздел"
              className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              onClick={() => { setRenaming(t.topic); setRenameDraft(t.title || t.topic) }}
            >
              <PencilLine className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              title="Править текст"
              className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              onClick={() => { setOpenTopic(t.topic); setTopicDraft(t.content_md || '') }}
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              title="Удалить раздел"
              disabled={saving}
              className="rounded p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
              onClick={() => removeTopic(t.topic)}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>

          {renaming === t.topic && (
            <div className="flex items-center gap-2 px-3 pb-2">
              <input
                autoFocus
                className="flex-1 rounded border bg-background px-2 py-1.5 text-sm"
                value={renameDraft}
                onChange={(e) => setRenameDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') renameTopic(t.topic)
                  if (e.key === 'Escape') setRenaming(null)
                }}
              />
              <Button size="sm" className="h-7 text-xs" onClick={() => renameTopic(t.topic)}>
                Сохранить
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setRenaming(null)}>
                Отмена
              </Button>
            </div>
          )}

          {openTopic === t.topic && (
            <div className="space-y-2 px-3 pb-3">
              <textarea
                className="h-64 w-full rounded-md border bg-background px-3 py-2 font-mono text-[13px]"
                value={topicDraft}
                onChange={(e) => setTopicDraft(e.target.value)}
              />
              <div className="flex gap-2">
                <Button size="sm" className="h-7 text-xs" disabled={saving} onClick={() => saveTopic(t.topic)}>
                  {saving && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                  Сохранить раздел
                </Button>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setOpenTopic(null)}>
                  Закрыть
                </Button>
              </div>
            </div>
          )}
        </div>
      ))}

      <div className="bg-slate-50/60 px-3 py-3">
        {!creating ? (
          <Button size="sm" variant="outline" className="h-7 gap-1.5 text-xs" onClick={() => setCreating(true)}>
            <Plus className="h-3 w-3" />
            Добавить раздел
          </Button>
        ) : (
          <div className="space-y-2">
            <input
              autoFocus
              className="w-full rounded border bg-background px-2 py-1.5 text-sm"
              placeholder={placeholder}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') addTopic() }}
            />
            <p className="text-[11px] text-muted-foreground">
              Название любое — технический ключ выведется сам.
            </p>
            <div className="flex gap-2">
              <Button size="sm" className="h-7 text-xs" disabled={saving || !newName.trim()} onClick={addTopic}>
                Создать
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setCreating(false)}>
                Отмена
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
